import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { AgentProfileOutcome, PhoneUpdateOutcome } from '../interface/agent-profile';
import { extractApiErrorMessage } from './api-error';

/**
 * Build-time switch, `REACT_APP_AGENT_PROFILE_ENDPOINT_ENABLED=true` — set in `.env.production`
 * (the staging image) and NOT in `.env.cluster` (the `-prod` image). Turn it on for a host
 * only once the api.core image carrying `GET /User/me/profile` is confirmed RUNNING — check the deployment's image tag, not the merge, and not the
 * branch. Measured 2026-08-22: the route was merged to `dev` and its image built, while
 * the gateway still answered 404 because the manifest bump and apply are manual.
 *
 * False means the page assembles itself from what the app already holds and makes no
 * request at all.
 */
export const AGENT_PROFILE_ENDPOINT_ENABLED = process.env.REACT_APP_AGENT_PROFILE_ENDPOINT_ENABLED === 'true';

/**
 * Standalone client, deliberately not the shared Redux thunk.
 *
 * `httpGetMethod` hardcodes `validateStatus: () => true`, and a 404 from ASP.NET routing
 * carries no `message` — so `extractApiErrorMessage` returns falsy, the helper throws, and
 * the catch dispatches a global red toast. On an endpoint that will 404 for as long as the
 * rollout takes, that is a toast on every Settings visit. The thunk also discards the
 * status code, so the page could not tell "not deployed" from "forbidden" from "retry".
 *
 * No 401→logout interceptor either: this is one page's data, and a failure here must never
 * end the agent's session.
 */
const agentProfileApi = axios.create({ baseURL: COREAPI });

agentProfileApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// api.core sometimes returns JSON with a Content-Type that stops axios auto-parsing.
agentProfileApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave it. `normalizeAgentProfile` rejects non-objects, which is
			// what stops an HTML error page reaching a lazy state initializer.
		}
	}
	return response;
});

export const getAgentProfile = async (): Promise<AgentProfileOutcome> => {
	if (!AGENT_PROFILE_ENDPOINT_ENABLED) return { reachable: false };

	try {
		const res = await agentProfileApi.get('/api/v1/User/me/profile', { validateStatus: () => true });
		return { reachable: true, httpStatus: res.status, body: res.data };
	} catch {
		return { reachable: false };
	}
};

/**
 * Change the agent's own phone number.
 *
 * Reads the error through `extractApiErrorMessage` because these services mix two shapes:
 * their own handlers return `{ message }`, while `[ApiController]` model validation returns
 * ProblemDetails with the text under `errors[""][0]`. Reading only `message` is how an
 * oversize upload once became an infinite retry loop.
 */
export const updateAgentPhone = async (phoneNumber: string): Promise<PhoneUpdateOutcome> => {
	if (!AGENT_PROFILE_ENDPOINT_ENABLED) return { reachable: false };

	try {
		const res = await agentProfileApi.put('/api/v1/User/me/phone', { phoneNumber }, { validateStatus: () => true });
		return {
			reachable: true,
			httpStatus: res.status,
			message: extractApiErrorMessage(res.data) || (res.data as { message?: string })?.message || null,
			body: res.data,
		};
	} catch {
		return { reachable: false };
	}
};
